import { useEffect, useState } from 'react';
import useAuth from './useAuth';
import usePermissions from './usePermissions';
import { listTeam } from '../services/team';
import { listSuppliers } from '../services/suppliers';
import {
  resolveSupplierScope,
  scopeAllowsSupplierAtPlant,
  scopedSupplierCodes,
} from '../utils/supplierScope';

/**
 * Resolves which plants the signed-in user is allowed to see.
 *
 * Plant-scoped roles (Regional Manager, Liaison Manager — see
 * `scopedToAssignedPlant` in usePermissions) may only see the plants whose
 * code appears on their own Proxemics Team record. Directors and Executives
 * are not scoped and see every plant.
 *
 * Returns:
 *   - scoped  : boolean — whether this user is restricted at all.
 *   - codes   : Set<string> of UPPERCASE plant codes the user may see, or
 *               null when not scoped (meaning "all plants").
 *   - loading : boolean — true while the user's Team record is being fetched.
 *   - allows(plantCode): convenience predicate. Always true when not scoped.
 *
 * Liaison Managers are ALSO scoped by supplier (per Jillian, Sep 2026 —
 * see utils/supplierScope.js): at a plant with one LM they see every
 * supplier; at a plant with several LMs they see only the suppliers
 * checked on their Team profile. Exposed as:
 *   - supplierScoped        : boolean — true for Liaison Managers.
 *   - supplierCodes         : Set<string> of UPPERCASE supplier codes the
 *                             LM may see anywhere, or null when not scoped.
 *   - allowsSupplier(code)  : predicate over that set. True when not scoped.
 *   - allowsSupplierAtPlant(plantCode, supplierCode): the precise check.
 *
 * Matches the signed-in email against the team roster the same way the
 * Dashboard does (Proxemics Email first, PersonalEmail fallback).
 */
export default function usePlantScope() {
  const { email } = useAuth();
  const { scopedToAssignedPlant, isLiaisonManager } = usePermissions();
  const [codes, setCodes] = useState(null);
  const [loading, setLoading] = useState(scopedToAssignedPlant);
  // Supplier scope (LMs only). `supplierScope` is the per-plant Map from
  // resolveSupplierScope(); `supplierCodes` is the flattened Set.
  const [supplierScope, setSupplierScope] = useState(null);
  const [supplierCodes, setSupplierCodes] = useState(null);

  useEffect(() => {
    // Not scoped → sees everything; nothing to fetch.
    if (!scopedToAssignedPlant) {
      setCodes(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    // Suppliers are only needed to flatten an LM's supplier scope; skip the
    // extra call for Regional Managers.
    Promise.all([listTeam(), isLiaisonManager ? listSuppliers() : Promise.resolve(null)])
      .then(([rows, suppliers]) => {
        if (cancelled) return;
        const lower = (email || '').toLowerCase();
        const me = (rows || []).find(
          (m) =>
            (m.Email && m.Email.toLowerCase() === lower) ||
            (m.PersonalEmail && m.PersonalEmail.toLowerCase() === lower),
        );
        const set = new Set(
          (me?.plantCodes || [])
            .filter(Boolean)
            .map((c) => String(c).toUpperCase()),
        );
        setCodes(set);
        if (isLiaisonManager) {
          // resolveSupplierScope keys off the record's PortalRole; a
          // Manager whose Team record isn't marked LiaisonManager (or is
          // missing) fails closed to an empty scope.
          const scope = me ? resolveSupplierScope(me, rows || []) : null;
          const effective = scope || new Map();
          setSupplierScope(effective);
          setSupplierCodes(scopedSupplierCodes(effective, suppliers || []));
        } else {
          setSupplierScope(null);
          setSupplierCodes(null);
        }
      })
      .catch(() => {
        // Fail closed: if we can't resolve the roster, a scoped user sees no
        // plants (and, for LMs, no suppliers) rather than accidentally
        // seeing all of them.
        if (!cancelled) {
          setCodes(new Set());
          if (isLiaisonManager) {
            setSupplierScope(new Map());
            setSupplierCodes(new Set());
          }
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scopedToAssignedPlant, isLiaisonManager, email]);

  const allows = (plantCode) => {
    if (!scopedToAssignedPlant) return true;
    if (!codes) return false;
    return codes.has(String(plantCode || '').toUpperCase());
  };

  const supplierScoped = Boolean(isLiaisonManager);
  const allowsSupplier = (supplierCode) => {
    if (!supplierScoped) return true;
    if (!supplierCodes) return false;
    return supplierCodes.has(String(supplierCode || '').trim().toUpperCase());
  };
  const allowsSupplierAtPlant = (plantCode, supplierCode) => {
    if (!supplierScoped) return true;
    if (!supplierScope) return false;
    return scopeAllowsSupplierAtPlant(supplierScope, plantCode, supplierCode);
  };

  return {
    scoped: scopedToAssignedPlant,
    codes,
    loading,
    allows,
    supplierScoped,
    supplierScope,
    supplierCodes,
    allowsSupplier,
    allowsSupplierAtPlant,
  };
}
